<template>
    <div>
        <div class="form-fixed-nav">
            <div class="form-fixed-nav__left">
                <a class="animated-arrow backwards hide-mobile" @click.prevent="back()">
                    <span class="the-arrow -left">
                        <span class="shaft"></span>
                        </span>
                        <span class="main">
                            <span class="text button-text text-primary">{{ $t('common.back') }}</span>
                        <span class="the-arrow -right">
                        <span class="shaft"></span>
                        </span>
                    </span>
                </a>
                <a class="form-fixed-nav__button-back button-text hide-desktop" @click.prevent="back()">{{ $t('common.back') }}</a>
            </div>
            <div class="form-fixed-nav__center form-fixed-nav__center__demo">
                <div class="form-fill-progress">
                    <div class="form-fill-progress__step step-success" @click.prevent="back()">
                        <p class="form-fill-progress__step__text">{{ $t('getting_started.template') }}</p>
                    </div>
                    <div class="form-fill-progress__step step-current step-2">
                        <p class="form-fill-progress__step__text">{{ $t('getting_started.customization') }}</p>
                    </div>
                    <div class="form-fill-progress__step step-disabled step-3">
                        <p class="form-fill-progress__step__text">{{ $t('getting_started.preview_and_next_steps') }}</p>                    
                    </div>
                </div>
            </div>
            <div class="form-fixed-nav__right">
                <button :disabled="!continueAllowed" class="form-fixed-nav__button-forward button-text hide-desktop" @click="showRegisterForm()">{{ $t('getting_started.preview') }}</button>
            </div>
        </div>

        <div class="container">
            <div class="form-header">
                <h3 class="form-header__heading text-primary">{{ $t('getting_started.customize_your_website') }}</h3>
                <p class="text-heading text-secondary l-460-desktop">{{ $t('getting_started.customize_your_website_description') }}</p>
            </div>
        </div>

        <div class="customize-website">
            <div class="customize-website-container">
                <h4 class="text-primary customize-website__heading">{{ $t('getting_started.add_property_address') }}</h4>
                <div class="form-container">
                    <div class="justify-content-center">
                        <div class="input-group">
                            <google-maps-autocomplete :select-first-on-enter="true" class="input" :placeholder="$t('listing_form.listing_details.type_address')" @place_changed="setPlace"></google-maps-autocomplete>
                            <label for="country" class="subtitle text-primary">{{ $t('listing_form.listing_details.find_your') }}</label>
                        </div>
                        <div class="input-group">
                            <input name="country" type="text" class="d-none">
                            <country-select ref="countrySelect" @updated="updateCountry" @remove="updateCountry('')" :country="info.country"></country-select>
                            <label for="country" class="subtitle text-primary">{{ $t('listing_form.listing_details.country') }}</label>
                        </div>

                        <div class="input-group">
                            <input type="text" name="street" class="input" id="street" v-model="info.street" @input="isTyping = true">
                            <label for="street" class="subtitle text-primary">{{ $t('listing_form.listing_details.street') }}*</label>
                        </div>

                        <div class="input-group">
                            <input type="text" name="city" class="input" id="address_line_2" v-model="info.city" @input="isTyping = true">
                            <label for="city" class="subtitle text-primary">{{ $t('listing_form.listing_details.city') }}*</label>
                        </div>

                        <div class="d-flex justify-content-between">
                            <div class="input-group w-60 mr-40">
                                <input type="text" name="state" class="input" id="state" v-model="info.state" @input="isTyping = true">
                                <label for="state" class="subtitle text-primary"><template v-if="info.country == 'United States'">{{ $t('listing_form.listing_details.state') }}*</template><template v-else>{{ $t('listing_form.listing_details.region_county') }}</template></label>
                            </div>

                            <div class="input-group w-40">
                                <input type="text" name="postcode" class="input" id="postcode" v-model="info.postcode" @input="isTyping = true">
                                <label for="postcode" class="subtitle text-primary"><template v-if="info.country == 'United States'">{{ $t('listing_form.listing_details.zip_code') }}*</template><template v-else>{{ $t('listing_form.listing_details.postal_code') }}*</template></label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="customize-website-container customize-website-container--last">
                <h4 class="text-primary customize-website__heading">{{ $t('getting_started.add_property_photos') }}</h4>
                <div class="mb-24 checkbox-no-photos">
                    <template v-if="!items.length">
                    <a-checkbox @change="photoCheckbox = arguments[0].target.checked" :style="checkboxStyle" class="subtitle text-secondary">{{ $t('getting_started.i_dont_have_any_photos_with_me') }}</a-checkbox>
                    </template>
                </div>
                <template v-if="items.length">
                    <div class="listing-photo-container">
                        <draggable v-model="items" v-bind="dragOptions" draggable=".draggable" class="minimal-container-row d-flex flex-wrap" @end="updateSort">
                            <div class="photo-component" v-for="photo in items" :key="photo.id" :class="{draggable: photo.is_uploaded, selected: selected == photo.id}" @click="selected=photo.id">
                                <template v-if="photo.is_uploading">
                                    <div class="photo-component__uploading photo-component__wrap">
                                        <div class="photo-component__upload-progress">
                                            <div class="photo-component__upload-progress__bar">
                                                <div class="photo-component__upload-progress__current" :style="{width: photo.progress + '%'}"></div>
                                            </div>
                                            <p class="text-center subtitle text-secondary">
                                                <template v-if="photo.progress < 100">{{ $t('getting_started.uploading') }}</template>
                                                <template v-else>{{ $t('getting_started.compressing_photo') }}</template>
                                            </p>
                                        </div>
                                    </div>
                                    <p class="text-primary">{{photo.name}}</p>
                                </template>
                                <template v-else-if="photo.is_uploaded">
                                    <div class="photo-component__wrap">
                                        <img :src="getThumbnail(photo)" :alt="photo.name">
                                        <div class="photo-component__buttons">
                                            <a @click="previewPhoto(photo)" class="button button-m button-outline-white preview-photo-desktop photo-component__button-center">{{ $t('common.preview') }}</a>
                                            <a @click="previewPhoto(photo)" class="text-white subtitle preview-photo-mobile photo-component__button-center">{{ $t('common.preview') }}</a>
                                            <a class="delete" @click="deletePhoto(photo.id)"><img src="/img/bin.svg" alt="delete"></a>
                                        </div>
                                    </div>
                                    <p class="text-primary">{{photo.name}}</p>
                                </template>
                                <template v-else>
                                    <div class="photo-component__alert">
                                        <div class="photo-component__alert__triangle">
                                            <img src='/img/alert-triangle.svg' alt="alert">
                                        </div>
                                        <template v-if="photo.reason">
                                            <p class="text-center subtitle text-alert">{{ photo.reason }}</p>
                                        </template>
                                        <template v-else>
                                            <p class="text-center subtitle text-alert">{{ $t('getting_started.unable_to_upload') }}</p>
                                        </template>
                                    </div>
                                    <p class="text-alert">{{photo.name}}</p>
                                </template>
                                
                            </div>
                            <uploader :upload_more_files="$t('listing_form.listing_photos.upload_more_photos')" :upload_files="$t('listing_form.listing_photos.upload_more_photos')" :is_disabled="uploadDisabled" slot="header" type="guest_listing_photos" class="photo-component media-uploader-small__container"></uploader> 
                        </draggable>
                    </div>
                </template>
                <template v-else>
                    <div class="large-container media-uploader-guest">
                        <uploader :caption="getCaption" :upload_files="$t('listing_form.listing_photos.upload_photos')" class="media-uploader-large__container" :is_large="true" type="guest_listing_photos" ></uploader>
                    </div>
                </template>
            </div>
            <div class="bottom-navigation-buttons-wrap">
                <button :disabled="!continueAllowed" class="button button-xl button-primary mb-32" @click="showRegisterForm()">{{ $t('getting_started.view_your_website') }}</button>
                <a class="button-text text-secondary" @click.prevent="showRegisterForm()">{{ $t('getting_started.or_skip_this_step') }}</a>
            </div>
        </div>
        <photo-modal></photo-modal>
        <confirmation-popup></confirmation-popup>
    </div>
</template>

<script>
    import _ from 'lodash';
    export default {
        props: {
            template: Number,
            back_route: {
                type: String,
                default: ''
            },
        },
        data() {
            return {
                template_id: '',
                refresh_sort: false,
                selected: '',
                info: {
                    country: '',
                    street: '',
                    city: '',
                    state: '',
                    postcode: '',
                },
                items: [],
                isTyping: false,
                checkboxStyle: {
                    fontSize: '16px',
                    color: '#798194'
                },
                photoCheckbox: false
            }
        },
        mounted() {
            this.getListingData();

            this.$root.$on('uploading_photo', (type, data) => {
                if (type == 'guest_listing_photos') {
                    this.updateUploadProgress(data);
                }
            })
            this.$root.$on('uploaded_photo', (type, data, id) => {
                if (type == 'guest_listing_photos') {
                    let index = this.items.findIndex(item => item.id === id);

                    if (index !== -1) {
                        this.$set(this.items[index], 'is_uploading', false)

                        Object.keys(data).forEach(function(key) {
                            this.$set(this.items[index], key, data[key])
                        }.bind(this));
                    } else {
                        this.items = this.items.concat(data);
                    }
                }

                this.refresh_sort = true;
            })

            this.$root.$on('confirmed', (type, data) => {
                if (type == DELETE_LISTING_PHOTO) {
                    let url =  `/guest/listing-photo`;

                    axios.delete(url, {data : {id: data.id}}).then(function (response) {
                        console.log('deleted');
                    }).then(function(response) {
                        this.items.splice(this.items.findIndex(item => item.id === data.id), 1)
                    }.bind(this));
                }
            })

            this.$root.$on('deleted_photo', (type, id) => {
                if (type == 'guest_listing_photos') {
                    this.items.splice(this.items.findIndex(item => item.id === id), 1)
                }
            })
        },
        methods: {
            back() {
                if (this.back_route != '') {
                    this.$router.push({
                        name: this.back_route
                    })
                } else {
                    javascript:history.go(-1);
                }
            },
            getListingData() {
                const url = '/guest/listing-data';
                
                return axios.get(url)
                // get data
                .then(function (response) {
                    let data = response.data.data;

                    // console.log(data);
                    this.info = data.info;
                    this.items = data.photos;
                    this.template_id = data.template_id;
                }.bind(this))
                .catch(error => {
                    console.log('catch error');
                    this.$router.push({
                        name: 'getting-started'
                    })
                });
            },
            saveDetails() {
                const url = `/guest/property-details`;
                
                return axios.post(url, this.info)
                // get data
                .then(function (response) {
                    // handle success
                }.bind(this));
            },
            updateCountry(value) {
                this.isTyping = true;
                this.info.country = value;
            },
            appendUploadedPhoto(data) {
                this.items = this.items.concat(data);
            },
            updateUploadProgress(data) {
                let index = this.items.findIndex(item => item.id === data.id);

                if (index !== -1) {
                    this.$set(this.items[index], 'progress', data.progress)
                } else {
                    this.items = this.items.concat(data);
                }
            },
            updateSort() {
                if (this.items.length < 2) {
                    return;
                }

                let sort_data = this.items.reduce(function (res, item) {
                    if (item.is_uploaded) {
                        res.push(item.id);
                    }
                    return res;
                }, []);

                let url =  `/guest/listing-photos/update-sort`;

                return axios.patch(url, {sort_data})
                    // get data
                    .then(function (response) {
                        console.log('resorted');
                    }.bind(this));
            },
            deletePhoto(id) {
                this.$root.$emit('show_confirmation', {
                    'title': this.$t('common.remove_photo'),
                    'description': this.$t('common.are_you_sure'),
                    'type': DELETE_LISTING_PHOTO,
                    'data': {
                        'id': id
                    },
                    'button_text': this.$t('common.remove')
                });
            },
            previewPhoto(photo) {
                this.$root.$emit('preview_photo', photo);
            },
            showRegisterForm() {
                this.$router.push({
                    name: 'register-user',
                    params: {
                        lang: i18n.locale == "en" ? null : i18n.locale
                    }
                })
            },
            setPlace(place) {
                let address = {};
                
                if(!place.address_components) {
                    return;
                }

                for (var i = 0; i < place.address_components.length; i++) {
                    var addressType = place.address_components[i].types[0];
                    
                    var addressValue = {
                        short_name: place.address_components[i].short_name,
                        long_name: place.address_components[i].long_name
                    }
        
                    address[addressType] = addressValue;
                }

                this.fillAddress(address);
            },
            fillAddress(address) {
                if (address.country) {
                    this.$refs.countrySelect.updateCountryByCode(address.country.short_name);

                    console.log(address)

                    if (address.country.short_name == 'GB' || address.country.short_name == 'SE' ) {
                        if (address.postal_town) {
                            this.info.city = address.postal_town.long_name;
                        }

                        if (address.administrative_area_level_2) {
                            this.info.state = address.administrative_area_level_2.long_name;
                        }

                    } else {
                        if (address.locality) {
                            this.info.city = address.locality.long_name;
                        } else if (address.sublocality_level_1) {
                            this.info.city = address.sublocality_level_1.long_name;
                        }

                        if (address.administrative_area_level_1) {
                            this.info.state = address.administrative_area_level_1.short_name;
                        }
                    }


                    if (address.postal_code) {
                        this.info.postcode = address.postal_code.long_name;
                    }

                    if (address.route) {
                        this.info.street = address.route.long_name;

                        if (address.street_number) {
                            this.info.street = this.info.street + ' ' + address.street_number.long_name;
                        }
                    }
                }
            },
            getThumbnail(photo) {
                return photo.thumb_path ? photo.thumb_path : photo.full_path;
            },
        },
        computed: {
            uploadDisabled() {
                return this.items.length < 20 ? false : true;
            },
            getCaption() {
                return `${this.$t('getting_started.upload_up_to_20_photos')}`;
            },
            continueAllowed() {
                console.log('ping');

                if (this.photoCheckbox == false && this.items.length < 1) {
                    return false;
                }

                let required = ['country', 'street', 'city', 'postcode'];

                if (this.info.country == 'United States') {
                    required.push('state');
                } else {
                    let index = required.indexOf('state');

                    if (index !== -1) {
                        array.splice(index, 1);
                    }
                }

                let i;
                
                for (i = 0; i < required.length; i++) {
                    if (this.info[required[i]] == "") {
                        return false;
                    }
                }

                return true;
            },
            dragOptions() {
                return {
                    animation: 200,
                    ghostClass: "ghost",
                };
            }
        },
        watch: {
            template: function(value) {
                this.template_id = value;
            },
            info: {
                handler: _.debounce(function() {
                    this.isTyping = false;
                }, 1000),
                deep: true
            },
            refresh_sort: {
                handler: _.debounce(function() {
                    if (this.refresh_sort) {
                        this.updateSort();
                    }

                    this.refresh_sort = false;
                }, 1000)
            },
            isTyping: function(value) {
                if (!value) {
                    this.saveDetails();
                }
            }
        }
    }
</script>
